import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import firebaseApp, { db } from 'services/firebase';

const LOCATIONS_COLLECTION = 'locations';
const VISITS_SUBCOLLECTION = 'visits';
const CLINIC_MESSAGES_SUBCOLLECTION = 'clinicMessages';

const CREATE_CHECKIN_FUNCTION = 'createCheckInVisit';
const SEND_BROADCAST_FUNCTION = 'sendClinicBroadcast';

/**
 * Public check-in URL for a clinic. The clinic id (a Firestore location doc id)
 * is the only thing encoded — the link is meant to be displayed/shared, so it
 * carries no secret.
 */
export const buildCheckInUrl = (clinicId) =>
  `${window.location.origin}/checkin/${clinicId}`;

/**
 * Public VisitStart URL for a clinic — what its waiting-room poster points at.
 *
 * Same shape and the same reasoning as the check-in link above: the location doc
 * id is the whole of it and carries no secret, because it is printed on a wall.
 * It authorises nothing on its own — landing there gets you the clinic's intake
 * questions and an anonymous session of your own, nothing about anyone else.
 *
 * `/visitstart` and not `/visit-start`: product names in this app are single
 * words (/carestart, /chartmind, /peerview) and hyphens are for phrases
 * (/masterchart, /medication-tracking).
 */
export const buildVisitStartUrl = (clinicId) =>
  `${window.location.origin}/visitstart/${clinicId}`;

/**
 * Patient self-check-in. The Cloud Function writes the visit with the admin SDK
 * (the visits subcollection is server-write-only) and always takes the patient
 * id from the auth context, so the client can't check anyone else in.
 */
export const submitCheckIn = async (clinicId) => {
  if (!clinicId) throw new Error('Missing clinic.');
  const callable = firebaseApp.functions().httpsCallable(CREATE_CHECKIN_FUNCTION);
  try {
    const { data } = await callable({ clinicId });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not check you in. Please try again.');
  }
};

/**
 * Live list of recent check-ins for a clinic (facility-side). Readable only by
 * clinic members per Firestore rules.
 */
export const subscribeRecentVisits = (clinicId, onChange, onError) => {
  if (!clinicId) return () => {};
  return db
    .collection(LOCATIONS_COLLECTION)
    .doc(clinicId)
    .collection(VISITS_SUBCOLLECTION)
    .orderBy('checkedInAt', 'desc')
    .limit(100)
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
};

/**
 * Broadcast a message to everyone who checked in to the clinic recently. The
 * Cloud Function resolves recipients and fans out into each patient's
 * server-write-only clinicMessages subcollection.
 */
export const sendClinicBroadcast = async ({ clinicId, body }) => {
  if (!clinicId) throw new Error('Missing clinic.');
  const callable = firebaseApp.functions().httpsCallable(SEND_BROADCAST_FUNCTION);
  try {
    const { data } = await callable({ clinicId, body });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not send the message. Please try again.');
  }
};

/**
 * Live list of clinic messages for the signed-in patient.
 */
export const subscribeMyClinicMessages = (uid, onChange, onError) => {
  if (!uid) return () => {};
  return db
    .collection('users')
    .doc(uid)
    .collection(CLINIC_MESSAGES_SUBCOLLECTION)
    .orderBy('createdAt', 'desc')
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      onError,
    );
};

/**
 * Mark one clinic message as read. Rules allow the owner to update only the
 * status/readAt fields on their own messages.
 */
export const markClinicMessageRead = async (uid, msgId) => {
  if (!uid || !msgId) return;
  await db
    .collection('users')
    .doc(uid)
    .collection(CLINIC_MESSAGES_SUBCOLLECTION)
    .doc(msgId)
    .update({
      status: 'read',
      readAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
};
